/**
 * WebSocket Message Types
 * Define all message types for type-safe communication
 */

import type { IVoucher } from 'src/types/voucher';

// ---------------------------------------------------------------------------
// Message type enum
// ---------------------------------------------------------------------------

export type WsMessageType =
  // Core
  | 'auth'
  | 'auth_response'
  | 'user_event'
  | 'ping'
  | 'pong'
  | 'config_selected'
  // Business events
  | 'transaction_created'
  | 'transaction_updated'
  | 'session_updated'
  | 'voucher_created'
  | 'voucher_updated'
  | 'products_from_erp'
  | 'roles_updated'
  | 'supply_created'
  | 'customer_created'
  | 'customer_updated'
  | 'notification_created'
  // System
  | 'error'
  | 'sync_request'
  | 'sync_response'
  // Init data streaming types
  | 'init_data_start'
  | 'init_data_chunk'
  | 'init_data_complete'
  | 'init_data_deferred';

export type WsConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

// ---------------------------------------------------------------------------
// Base message interface
// ---------------------------------------------------------------------------

export interface WsBaseMessage {
  type: WsMessageType;
  timestamp?: number;
  requestId?: string;
}

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

export interface WsAuthMessage extends WsBaseMessage {
  type: 'auth';
  user: {
    id: string;
    email?: string;
    [key: string]: any;
  };
  key: string | null;
  configSelected?: string;
}

export interface WsConfigSelectedMessage extends WsBaseMessage {
  type: 'config_selected';
  configSelected: string;
}

export interface WsAuthResponseMessage extends WsBaseMessage {
  type: 'auth_response';
  success: boolean;
  key?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// User events (connection status)
// ---------------------------------------------------------------------------

export interface WsUserEventMessage extends WsBaseMessage {
  type: 'user_event';
  message: string;
  key?: string;
}

// ---------------------------------------------------------------------------
// Transaction events
// ---------------------------------------------------------------------------

export interface WsTransactionCreatedMessage extends WsBaseMessage {
  type: 'transaction_created';
  message: 'TRANSACTION_CREATED';
  data: {
    id: string;
    type?: 'sale' | 'return' | 'refund' | string;
    [key: string]: any;
  };
}

export interface WsTransactionUpdatedMessage extends WsBaseMessage {
  type: 'transaction_updated';
  message: 'TRANSACTION_UPDATED';
  data: {
    id: string;
    type?: 'sale' | 'return' | 'refund' | string;
    [key: string]: any;
  };
}

// ---------------------------------------------------------------------------
// Session update events
// ---------------------------------------------------------------------------

export interface WsSessionUpdatedMessage extends WsBaseMessage {
  type: 'session_updated';
  message: 'SESSION_UPDATED';
  data: {
    id: string;
    currentAmount?: number;
    totalSales?: number;
    totalRefunds?: number;
    openingAmount?: number;
    summary?: {
      currentAmount: number;
      totalSales: number;
      totalRefunds: number;
      openingAmount: number;
    };
    [key: string]: any;
  };
}

// ---------------------------------------------------------------------------
// Voucher events (Phase 3)
// ---------------------------------------------------------------------------

export interface WsVoucherCreatedMessage extends WsBaseMessage {
  type: 'voucher_created';
  message: 'VOUCHER_CREATED';
  data: {
    id: string;
    code: string;
    customerId?: string;
    customerName?: string;
    type?: IVoucher['type'];
    totalAmount: number;
    remainingAmount: number;
    status: IVoucher['status'];
    sourceTransactionId?: string;
    [key: string]: any;
  };
}

export interface WsVoucherUpdatedMessage extends WsBaseMessage {
  type: 'voucher_updated';
  message: 'VOUCHER_UPDATED';
  data: {
    id: string;
    remainingAmount: number;
    status: IVoucher['status'];
    usedAt?: string;
    transactionId?: string;
    [key: string]: any;
  };
}

// ---------------------------------------------------------------------------
// Return events (Phase 3)
// ---------------------------------------------------------------------------

export interface WsReturnCreatedMessage extends WsBaseMessage {
  type: 'return_created';
  message: 'RETURN_CREATED';
  data: {
    id: string;
    transactionNumber: string;
    returnAuthNumber?: string;
    sourceType: 'INVOICE' | 'NO_SALE';
    customerId?: string;
    customerName?: string;
    totalAmount: number;
    items: Array<{
      productId: string;
      sku: string;
      name: string;
      quantity: number;
      unitPrice: number;
    }>;
    refunds?: Array<{
      paymentMethod: string;
      amount: number;
    }>;
    createdAt: string;
    [key: string]: any;
  };
}

export interface WsReturnUpdatedMessage extends WsBaseMessage {
  type: 'return_updated';
  message: 'RETURN_UPDATED';
  data: {
    id: string;
    transactionNumber: string;
    status?: string;
    [key: string]: any;
  };
}

// ---------------------------------------------------------------------------
// Products from ERP
// ---------------------------------------------------------------------------

export interface WsProductsFromErpMessage extends WsBaseMessage {
  type: 'products_from_erp';
  message: 'PRODUCTS_FROM_ERP';
  data: any[];
  fieldsSearchable?: string[];
}

// ---------------------------------------------------------------------------
// Roles updated
// ---------------------------------------------------------------------------

export interface WsRolesUpdatedMessage extends WsBaseMessage {
  type: 'roles_updated';
  message: 'roles updated';
  userId?: string;
}

// ---------------------------------------------------------------------------
// Supply created
// ---------------------------------------------------------------------------

export interface WsSupplyCreatedMessage extends WsBaseMessage {
  type: 'supply_created';
  message: 'supply created';
  data?: any;
}

// ---------------------------------------------------------------------------
// Customer created/updated
// ---------------------------------------------------------------------------

export interface WsCustomerCreatedMessage extends WsBaseMessage {
  type: 'customer_created' | 'customer_updated';
  message: 'CUSTOMER_CREATED' | 'CUSTOMER_UPDATED';
  data: {
    id?: string;
    email?: string;
    [key: string]: any;
  };
  source?: string;
}

export interface WsNotificationCreatedMessage extends WsBaseMessage {
  type: 'notification_created';
  message: 'NOTIFICATION_CREATED';
  source: 'netsuite';
  data: {
    id: string;
    title: string;
    message: string;
    status: 'unread' | 'read' | 'archived';
    severity: 'info' | 'warning' | 'error';
    recordModel: string;
    recordId: string;
    createdAt: string;
    [key: string]: any;
  };
}

// ---------------------------------------------------------------------------
// Error message
// ---------------------------------------------------------------------------

export interface WsErrorMessage extends WsBaseMessage {
  type: 'error';
  error: string;
  code?: string;
}

// ---------------------------------------------------------------------------
// Heartbeat
// ---------------------------------------------------------------------------

export interface WsPingMessage extends WsBaseMessage {
  type: 'ping';
}

export interface WsPongMessage extends WsBaseMessage {
  type: 'pong';
  timestamp: number;
  serverTimestamp?: number;
}

// ---------------------------------------------------------------------------
// Sync messages (for offline mode)
// ---------------------------------------------------------------------------

export interface WsSyncRequestMessage extends WsBaseMessage {
  type: 'sync_request';
  entity: string;
  lastSyncTimestamp?: number;
}

export interface WsSyncResponseMessage extends WsBaseMessage {
  type: 'sync_response';
  entity: string;
  data: any[];
  deletedIds?: string[];
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Init data streaming messages
// ---------------------------------------------------------------------------

export interface WsInitDataStartMessage extends WsBaseMessage {
  type: 'init_data_start';
  entity: 'products' | 'users';
  totalItems: number;
  totalChunks: number;
}

export interface WsInitDataChunkMessage extends WsBaseMessage {
  type: 'init_data_chunk';
  entity: 'products' | 'users';
  chunkIndex: number;
  totalChunks: number;
  data: any[];
  isLast: boolean;
}

export interface WsInitDataCompleteMessage extends WsBaseMessage {
  type: 'init_data_complete';
  entity: 'products' | 'users';
  totalItems: number;
}

export interface WsInitDataDeferredMessage extends WsBaseMessage {
  type: 'init_data_deferred';
  entity: 'products' | 'users';
  totalItems: number;
  reason: 'too_large' | string;
}

// ---------------------------------------------------------------------------
// Union types
// ---------------------------------------------------------------------------

export type WsIncomingMessage =
  | WsAuthResponseMessage
  | WsUserEventMessage
  | WsTransactionCreatedMessage
  | WsTransactionUpdatedMessage
  | WsSessionUpdatedMessage
  | WsVoucherCreatedMessage
  | WsVoucherUpdatedMessage
 | WsReturnCreatedMessage
 | WsReturnUpdatedMessage
  | WsProductsFromErpMessage
  | WsRolesUpdatedMessage
  | WsSupplyCreatedMessage
  | WsCustomerCreatedMessage
  | WsNotificationCreatedMessage
  | WsErrorMessage
  | WsPongMessage
  | WsSyncResponseMessage
  | WsInitDataStartMessage
  | WsInitDataChunkMessage
  | WsInitDataCompleteMessage
  | WsInitDataDeferredMessage;

export type WsOutgoingMessage =
  | WsAuthMessage
  | WsConfigSelectedMessage
  | WsPingMessage
  | WsSyncRequestMessage;

// ---------------------------------------------------------------------------
// Helper type guard functions
// ---------------------------------------------------------------------------

export const isAuthResponseMessage = (msg: WsIncomingMessage): msg is WsAuthResponseMessage =>
  msg.type?.toLowerCase() === 'auth_response';

export const isUserEventMessage = (msg: WsIncomingMessage): msg is WsUserEventMessage =>
  msg.type?.toLowerCase() === 'user_event';

export const isTransactionCreatedMessage = (msg: WsIncomingMessage): msg is WsTransactionCreatedMessage =>
  msg.type?.toLowerCase() === 'transaction_created' ||
  (msg as any).message === 'TRANSACTION_CREATED';

export const isTransactionUpdatedMessage = (msg: WsIncomingMessage): msg is WsTransactionUpdatedMessage =>
  msg.type?.toLowerCase() === 'transaction_updated' ||
  (msg as any).message === 'TRANSACTION_UPDATED';

export const isSessionUpdatedMessage = (msg: WsIncomingMessage): msg is WsSessionUpdatedMessage =>
  msg.type?.toLowerCase() === 'session_updated' || (msg as any).message === 'SESSION_UPDATED';

export const isVoucherCreatedMessage = (msg: WsIncomingMessage): msg is WsVoucherCreatedMessage =>
  msg.type?.toLowerCase() === 'voucher_created' || (msg as any).message === 'VOUCHER_CREATED';

export const isVoucherUpdatedMessage = (msg: WsIncomingMessage): msg is WsVoucherUpdatedMessage =>
  msg.type?.toLowerCase() === 'voucher_updated' || (msg as any).message === 'VOUCHER_UPDATED';

export const isReturnCreatedMessage = (msg: WsIncomingMessage): msg is WsReturnCreatedMessage =>
  msg.type?.toLowerCase() === 'return_created' || (msg as any).message === 'RETURN_CREATED';

export const isReturnUpdatedMessage = (msg: WsIncomingMessage): msg is WsReturnUpdatedMessage =>
  msg.type?.toLowerCase() === 'return_updated' || (msg as any).message === 'RETURN_UPDATED';

export const isProductsFromErpMessage = (msg: WsIncomingMessage): msg is WsProductsFromErpMessage =>
  msg.type?.toLowerCase() === 'products_from_erp' || (msg as any).message === 'PRODUCTS_FROM_ERP';

export const isRolesUpdatedMessage = (msg: WsIncomingMessage): msg is WsRolesUpdatedMessage =>
  msg.type?.toLowerCase() === 'roles_updated' || (msg as any).message === 'roles updated';

export const isSupplyCreatedMessage = (msg: WsIncomingMessage): msg is WsSupplyCreatedMessage =>
  msg.type?.toLowerCase() === 'supply_created' || (msg as any).message === 'supply created';

export const isCustomerCreatedMessage = (msg: WsIncomingMessage): msg is WsCustomerCreatedMessage =>
  msg.type?.toLowerCase() === 'customer_created' ||
  msg.type?.toLowerCase() === 'customer_updated' ||
  (msg as any).message === 'CUSTOMER_CREATED' ||
  (msg as any).message === 'CUSTOMER_UPDATED';

export const isNotificationCreatedMessage = (
  msg: WsIncomingMessage
): msg is WsNotificationCreatedMessage =>
  msg.type?.toLowerCase() === 'notification_created' ||
  (msg as any).message === 'NOTIFICATION_CREATED';

export const isErrorMessage = (msg: WsIncomingMessage): msg is WsErrorMessage =>
  msg.type?.toLowerCase() === 'error';

// Init data streaming type guards
export const isInitDataStartMessage = (msg: WsIncomingMessage): msg is WsInitDataStartMessage =>
  msg.type?.toLowerCase() === 'init_data_start';

export const isInitDataChunkMessage = (msg: WsIncomingMessage): msg is WsInitDataChunkMessage =>
  msg.type?.toLowerCase() === 'init_data_chunk';

export const isInitDataCompleteMessage = (msg: WsIncomingMessage): msg is WsInitDataCompleteMessage =>
  msg.type?.toLowerCase() === 'init_data_complete';

export const isInitDataDeferredMessage = (msg: WsIncomingMessage): msg is WsInitDataDeferredMessage =>
  msg.type?.toLowerCase() === 'init_data_deferred';
